import { useComponentState } from "@faedev/dynamic-values";
import { composeClassNames } from "@faedev/editor-utils";
import Link from "next/link";
import { useRouter } from "next/router";
import { CSSProperties, useRef, useState } from "react";
import { Card, ProgressBar } from "react-bootstrap";
import ClassSerialNo from "src/components/ClassSerialNo";
import Duration from "src/components/Duration";
import Favorite from "src/components/Favorite";
import ModalVideo from "src/components/ModalVideo";
import Paid from "src/components/Paid";
import Property from "src/components/Property";
import Rating from "src/components/Rating";
import constants from "src/constants";
import getLink from "src/functions/getLink";
import translate from "src/functions/translate";
import { sLink } from "src/styles/sLink.module.scss";
import { sVideoCard } from "src/styles/sVideoCard.module.scss";
import { sVideoCardBody } from "src/styles/sVideoCardBody.module.scss";
import { sVideoCardFooter } from "src/styles/sVideoCardFooter.module.scss";
import { sVideoCardOverlay } from "src/styles/sVideoCardOverlay.module.scss";
import { sVideoProgress } from "src/styles/sVideoProgress.module.scss";
import { ApiVideoCard } from "src/types/ApiVideoCard";
import { VariantSize } from "src/types/VariantSize";
import { VariantVideoPreview } from "src/types/VariantVideoPreview";

type Props = {
  style?: CSSProperties | null | undefined;
  className?: (string | string) | null | undefined;
  size: VariantSize;
  type: VariantVideoPreview;
  data?: ApiVideoCard;
  serialNo?: number | null | undefined;
  bunch?: number | null | undefined;
};

function VideoCard({
  style,
  className,
  size,
  type,
  data,
  serialNo,
  bunch,
}: Props) {
  const { locale: lang } = useRouter();
  const targetRef = useRef();
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const openModalVideo = useComponentState(false);

  const videoImgUrlLarge = data
    ? data.thumbnail
      ? data.thumbnail.formats
        ? data.thumbnail.formats.large
          ? !constants.Images.videophotoFullSize
            ? data.thumbnail.formats.large.url
            : data.thumbnail.url
          : data.thumbnail.url
        : null
      : null
    : null;
  const videoImgUrlMedium = data
    ? data.thumbnail
      ? data.thumbnail.formats
        ? data.thumbnail.formats.medium
          ? !constants.Images.videophotoFullSize
            ? data.thumbnail.formats.medium.url
            : data.thumbnail.url
          : data.thumbnail.url
        : null
      : null
    : null;
  const videoImgUrlSmall = data
    ? data.thumbnail
      ? data.thumbnail.formats
        ? data.thumbnail.formats.small
          ? !constants.Images.videophotoFullSize
            ? data.thumbnail.formats.small.url
            : data.thumbnail.url
          : data.thumbnail.url
        : null
      : null
    : null;

  return data && data.video && data.thumbnail ? (
    <>
      <Card
        className={composeClassNames([
          sVideoCard,
          type,
          size,
          className,
          "video",
        ])}
        id={`video${data.id}`}
      >
        <div className="card-img-wrapper">
          <Card.Img
            src={constants.Paths.imagesUrl + videoImgUrlMedium}
            ref={targetRef}
            alt={data.title}
          />
          <Card.ImgOverlay
            className={composeClassNames([
              sVideoCardOverlay,
              type,
              size,
              "video-overlay",
            ])}
          >
            <Rating
              value={data.rating}
              size={size}
              className={`video-rating`}
              colors={{
                default: constants.Theme.white,
                active: constants.Theme.white,
              }}
            />
            <Favorite
              setByUser={true}
              size={size}
              className={`video-favorite`}
              colors={{
                default: constants.Theme.white,
                active: constants.Theme.white,
              }}
              value={true}
            />
            {(() => {
              switch (type) {
                case `preview`:
                  return (
                    <Paid
                      paid={data.paid}
                      isPaid={data.isPaid}
                      subscription={data.subscription}
                      size={size}
                      className={`video-paid`}
                    />
                  );
                case `class`:
                  return (
                    <ClassSerialNo
                      value={serialNo}
                      length={bunch}
                      className={`video-class-serial-no`}
                    />
                  );
              }
            })()}
            <Duration
              size={size}
              className={`video-content-type`}
              duration={data.duration}
            />
            <Link
              href={
                data.paid || !constants.Show.contentSamplesInModal
                  ? getLink(lang, data.contentType, data.url)
                  : `#video${data.id}`
              }
              passHref
              locale={lang}
            >
              <Card.Link
                className={composeClassNames([sLink, `img-link`])}
                onClick={
                  !data.paid && constants.Show.contentSamplesInModal
                    ? function () {
                        return openModalVideo.change(true);
                      }
                    : null
                }
              />
            </Link>
          </Card.ImgOverlay>
        </div>
        <Card.Body
          className={composeClassNames([sVideoCardBody, size, "video-body"])}
        >
          {data.played > 0 ? (
            <ProgressBar
              max={100}
              min={1}
              className={composeClassNames([
                sVideoProgress,
                "sm",
                "video-progress",
              ])}
              now={50}
            />
          ) : null}
          <Card.Title>
            <Link
              href={
                data.paid || !constants.Show.contentSamplesInModal
                  ? getLink(lang, data.contentType, data.url)
                  : `#video${data.id}`
              }
              passHref
              locale={lang}
            >
              <Card.Link
                className={composeClassNames([sLink, `video-link`])}
                onClick={
                  !data.paid && constants.Show.contentSamplesInModal
                    ? function () {
                        return openModalVideo.change(true);
                      }
                    : null
                }
              >
                {data.title}
              </Card.Link>
            </Link>
          </Card.Title>
          {(function () {
            switch (type) {
              case `preview`:
                return (
                  <Card.Text>
                    <Link
                      href={getLink(lang, `profile`, data.profile.url)}
                      passHref
                      locale={lang}
                    >
                      <Card.Link
                        className={composeClassNames([sLink, `video-link`])}
                      >
                        {data.profile.nickname}
                      </Card.Link>
                    </Link>
                    {data.practice &&
                    data.practice.style &&
                    data.practice.style[0]?.type
                      ? data.practice.style.length > 1
                        ? ` • ${translate(
                            data.practice.style[0].type.replace(/_/g, ` `),
                            lang
                          )}(+${data.practice.style.length})`
                        : ` • ${translate(
                            data.practice.style[0].type.replace(/_/g, ` `),
                            lang
                          )}`
                      : ``}
                  </Card.Text>
                );
              case `serie`:
                return (
                  <Card.Text className={`video-label`}>{data.label}</Card.Text>
                );
              case `class`:
                return (
                  <Card.Text className={`video-label`}>{data.label}</Card.Text>
                );
            }
          })()}
        </Card.Body>
        {(function () {
          switch (type) {
            case `preview`:
              return data.practice &&
                (data.practice.difficulty ||
                  data.practice.focus ||
                  data.views > 0) ? (
                <Card.Footer
                  className={composeClassNames([
                    sVideoCardFooter,
                    size,
                    "video-footer",
                  ])}
                >
                  {data.practice && data.practice.difficulty ? (
                    <Property
                      size={size}
                      className={`video-property`}
                      type={`difficulty`}
                      showIcon={true}
                      translation={true}
                      value={data.practice ? data.practice.difficulty : null}
                    />
                  ) : (
                    <div></div>
                  )}
                  <Property
                    size={size}
                    className={`video-property`}
                    type={`focus`}
                    showIcon={true}
                    translation={true}
                    value={
                      data.practice &&
                      data.practice.focus &&
                      data.practice.focus[0]?.type
                        ? data.practice.focus[0].type
                        : null
                    }
                  />
                  <Property
                    size={size}
                    className={`video-property`}
                    type={`views`}
                    showIcon={true}
                    value={
                      data.views
                        ? String(Math.round(data.views / 1000)) + ` k`
                        : null
                    }
                  />
                </Card.Footer>
              ) : null;
            case `serie`:
              return null;
            case `class`:
              return null;
          }
        })()}
      </Card>
      {openModalVideo.value ? (
        <>
          <ModalVideo openModalVideo={openModalVideo} data={data} />
        </>
      ) : null}
    </>
  ) : null;
}

export default VideoCard;
